import { CF_API_URL } from '../../config/env'
import { sleepMs } from '../orderUtils'
import { getFirebase } from '../firebase/client'

const PROTECTED_ACTIONS = new Set([
  'updateLastViewer',
  'saveParticipantFirestore',
  'saveCustomerFirestore',
  'notifyOrderSubmitted',
  'switchToCodPayment',
])

function parseCfResponse(raw: string): Record<string, unknown> {
  try {
    return JSON.parse(raw || '{}') as Record<string, unknown>
  } catch {
    throw new Error('Cloud Functions ตอบกลับไม่ถูกต้อง — ตรวจสอบการเชื่อมต่อ')
  }
}

function callOrdercusCfXhr(body: string, idToken: string): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', CF_API_URL, true)
    xhr.setRequestHeader('Content-Type', 'application/json')
    if (idToken) xhr.setRequestHeader('Authorization', `Bearer ${idToken}`)
    xhr.timeout = 45000
    xhr.onload = () => {
      try {
        resolve(parseCfResponse(xhr.responseText || '{}'))
      } catch (e) {
        reject(e)
      }
    }
    xhr.onerror = () => reject(new Error('เชื่อมต่อ Cloud Functions ไม่ได้'))
    xhr.ontimeout = () => reject(new Error('เชื่อมต่อ Cloud Functions หมดเวลา'))
    xhr.send(body)
  })
}

async function callOrdercusCfOnce(
  action: string,
  payload: Record<string, unknown>,
  useXhr: boolean,
): Promise<Record<string, unknown>> {
  const body = JSON.stringify({ ...payload, action })
  const idToken = PROTECTED_ACTIONS.has(action) ? await (await getFirebase()).getIdToken() : ''
  if (useXhr) return callOrdercusCfXhr(body, idToken)

  let response: Response
  try {
    response = await fetch(CF_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body,
    })
  } catch (netErr) {
    const netMsg = netErr instanceof Error ? netErr.message : String(netErr)
    throw new Error(`เชื่อมต่อ Cloud Functions ไม่ได้ (${netMsg})`)
  }
  const raw = await response.text()
  return parseCfResponse(raw)
}

export async function callOrdercusCf(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const delays = [0, 1200, 2800]
  let lastErr: Error | null = null
  for (let i = 0; i < delays.length; i++) {
    if (delays[i]) await sleepMs(delays[i])
    try {
      const useXhr = i === delays.length - 1
      const res = await callOrdercusCfOnce(action, payload, useXhr)
      if (res?.status === 'error') {
        if (res.orderNotFound === true || res.orderInvalid === true) return res
        const msg = String(res.message || '')
        if (msg.includes('Invalid action')) return res
        lastErr = new Error(msg)
        continue
      }
      return res
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e))
    }
  }
  throw lastErr || new Error('เชื่อมต่อ Cloud Functions ไม่ได้')
}

export function isCfStepOk(res: Record<string, unknown> | null | undefined): boolean {
  if (!res) return false
  if (res.status === 'success' || res.status === 'skip') return true
  if (res.status === 'error') return false
  return true
}
